import { useState, useEffect } from 'react'
import { get } from '../../../../utils/Api'
import { Icon } from '@iconify/react'
import { useGlobalContext } from '../../../../utils/Context'
import PageLoader from '../../../../components/loader/PageLoader'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { DateRangeSelector } from '../../../../components/dateRange/DateRangeSelector'
import { startOfDay, endOfDay } from 'date-fns'
import { generateAnalyticsPDF } from '../../../../utils/generatePDF'
import { BarChart } from './BarChart'

function Analytics() {
  const [analytics, setAnalytics] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const { notify } = useGlobalContext()

  const fetchAnalytics = async (from: string, to: string, location: string) => {
    setLoading(true)
    try {
      const response = await get(
        `admin/analytics?from=${from}&to=${to}&location=${location}`
      )
      if (response.status !== 200) {
        notify('error', 'Failed to fetch analytics')
        return
      }
      setAnalytics(response.data)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    fetchAnalytics(from, to, '')
  }, [])
  const [from, setFrom] = useState<any>(startOfDay(new Date()))
  const [to, setTo] = useState(endOfDay<any>(new Date()))

  const handleDateRange = (start: string, end: string, location: string) => {
    setFrom(start)
    setTo(end)
    fetchAnalytics(start, end, location)
    // Call your API here with the query
  }
  const handleDownload = () => {
    const data = analytics
    generateAnalyticsPDF({ ...data, from, to })
  }

  //Download Manifest
  const [location, setLocation] = useState<string>('')

  const [dates, setDates] = useState({
    from: '',
    to: '',
  })
  const baseURL = (import.meta as any).env.VITE_BASE_URL

  function buildQueryString(opts: any) {
    const params = new URLSearchParams()
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.format) params.set('format', opts.format)
    if (location) {
      params.set('location', location)
    }
    return params.toString()
  }

  async function handleDownloadManifest(format = 'excel') {
    const qs = buildQueryString({
      from: dates.from,
      to: dates.to,
      format,
      location,
    })
    const token = sessionStorage.getItem('token')
    const url = `${baseURL}/admin/manifest/export?${qs}`

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()

      const fileName = `Manifest_${dates.from}_to_${dates.to}_for_${location}`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      notify('error', 'Could not download bookings')
    }
  }
  async function handleDownloadSnapshot(type: any) {
    let date = new Date().toISOString().slice(0, 10)
    if (dates.to.length > 0) {
      date = dates.to
    }
    const token = sessionStorage.getItem('token')
    const url = `${baseURL}/admin/snapshots/${type}?date=${date}`

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()

      const fileName = `${type}_Snapshot_on_${date}`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      notify('error', 'Could not download snapshot')
    }
  }

  return (
    <div className='home'>
      <Tooltip id='my-tooltip' />
      <DateRangeSelector
        onChange={(startDate, endDate, location) => {
          handleDateRange(startDate, endDate, location)
          setDates({ from: startDate, to: endDate })
          setLocation(location)
        }}
      />
      {loading ? (
        <PageLoader position='center' />
      ) : (
        <>
          <div className='home_tag flex justify-between'>
            {dates.from === dates.to ? (
              <div className='flex gap-16'>
                <button onClick={() => handleDownloadSnapshot('business')}>
                  Business Snapshot
                </button>
                <button onClick={() => handleDownloadSnapshot('users')}>
                  Users Snapshot
                </button>
              </div>
            ) : (
              <span></span>
            )}

            <div className='flex gap-16'>
              {dates.from && dates.to && location && (
                <button onClick={() => handleDownloadManifest()}>
                  Download Manifest
                </button>
              )}
              <button onClick={() => handleDownload()}>Download report</button>
            </div>
          </div>
          <div className='home-head'>
            <div className='biz flex justify-between flex-wrap'>
              <div className='card'>
                <p
                  className='flex info items-center'
                  data-tooltip-id='my-tooltip'
                  data-tooltip-content='Total Valid Bookings (Delivered + Pending)'
                >
                  Total Valid Bookings
                  <Icon icon='material-symbols:info-outline' />
                </p>
                <h2>{analytics.summary.totalBookings.toLocaleString()}</h2>
              </div>

              <div className='card'>
                <p>Total Booking Charge</p>
                <h2>
                  ₦
                  {parseFloat(
                    analytics.summary.totalCharge.toFixed(2)
                  ).toLocaleString()}{' '}
                </h2>
              </div>
              <div className='card'>
                <p>Average Booking Charge</p>
                <h2>
                  ₦
                  {parseFloat(
                    analytics.summary.averageCharge.toFixed(2)
                  ).toLocaleString()}{' '}
                </h2>
              </div>
              <div className='card'>
                <p>Delivered Bookings</p>
                <h2>{analytics.summary.deliveredBookings.toLocaleString()}</h2>
              </div>
              <div className='card'>
                <p>Canceled Bookings</p>
                <h2>{analytics.summary.canceledBookings.toLocaleString()}</h2>
              </div>
              <div className='card'>
                <p>Pending Bookings</p>
                <h2>
                  {parseFloat(
                    analytics.summary.pendingBookings.toFixed(2)
                  ).toLocaleString()}{' '}
                </h2>
              </div>
              <div className='card'>
                <p>Created User</p>
                <h2>
                  {parseFloat(
                    analytics.summary.userCreatedCount.toFixed(2)
                  ).toLocaleString()}{' '}
                </h2>
              </div>
            </div>
          </div>
          <div className='home_tables flex'>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Bookings By Channel</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Count</th>
                    <th>Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.breakdowns.byChannel.map((item: any) => (
                    <tr key={item._id}>
                      <td>{item._id}</td>
                      <td className='count'>{item.count}</td>
                      <td className='count'>
                        ₦
                        {parseFloat(
                          item.totalValue.toFixed(2)
                        ).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Bookings By Delivery Type</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Delivery Type</th>
                    <th>Count</th>
                    <th>Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.breakdowns.byDeliveryType.map((item: any) => (
                    <tr key={item._id}>
                      <td>{item._id}</td>
                      <td className='count'>{item.count}</td>
                      <td className='count'>
                        ₦
                        {parseFloat(
                          item.totalValue.toFixed(2)
                        ).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* <div className='half-table'>
              <div className='half_top'>
                <h2>Bookings By Shipment Type</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Shipment Type</th>
                    <th>Count</th>
                    <th>Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.breakdowns.byShipmentType.map((item: any) => (
                    <tr key={item._id}>
                      <td>{item._id}</td>
                      <td className='count'>{item.count}</td>
                      <td className='count'>
                        ₦
                        {parseFloat(
                          item.totalValue.toFixed(2)
                        ).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div> */}
          </div>
          <div className='home_tables flex'>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Users By Booking Count</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topUsers.byCount.map((item: any) => (
                    <tr key={item.id}>
                      <td className='count'>{item.name}</td>
                      <td className='count'>
                        {parseFloat(item.count.toFixed(2)).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Users By Booking Amount</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Delivery Type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topUsers.byCharge.map((item: any) => (
                    <tr key={item.id}>
                      <td className='count'>{item.name}</td>
                      <td className='count'>
                        ₦
                        {parseFloat(
                          item.totalCharge.toFixed(2)
                        ).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className='home_tables flex'>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Businesses By Booking Count</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topBusinesses.byCount.map((item: any) => (
                    <tr key={item.id}>
                      <td className='count'>{item.name}</td>
                      <td className='count'>
                        {parseFloat(item.count.toFixed(2)).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Businesses By Booking Amount</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Delivery Type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topBusinesses.byCharge.map(
                    (item: any) => (
                      <tr key={item.id}>
                        <td className='count'>{item.name}</td>
                        <td className='count'>
                          ₦
                          {parseFloat(
                            item.totalCharge.toFixed(2)
                          ).toLocaleString()}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className='home_tables flex'>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Users By Floating Booking Count</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topFloatingUsers.byCount.map(
                    (item: any) => (
                      <tr key={item.id}>
                        <td className='count'>{item.name}</td>
                        <td className='count'>
                          {parseFloat(item.count.toFixed(2)).toLocaleString()}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Users By Booking Amount</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Delivery Type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topFloatingUsers.byCharge.map(
                    (item: any) => (
                      <tr key={item.id}>
                        <td className='count'>{item.name}</td>
                        <td className='count'>
                          ₦
                          {parseFloat(
                            item.totalCharge.toFixed(2)
                          ).toLocaleString()}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* <div className='home_tables flex'>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Businesses By Floating Booking Count</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topFloatingBusinesses.byCount.map(
                    (item: any) => (
                      <tr key={item.id}>
                        <td className='count'>{item.name}</td>
                        <td className='count'>
                          {parseFloat(item.count.toFixed(2)).toLocaleString()}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div className='half-table'>
              <div className='half_top'>
                <h2>Top Businesses By Booking Amount</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Delivery Type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.insights.topFloatingBusinesses.byCharge.map(
                    (item: any) => (
                      <tr key={item.id}>
                        <td className='count'>{item.name}</td>
                        <td className='count'>
                          ₦
                          {parseFloat(
                            item.totalCharge.toFixed(2)
                          ).toLocaleString()}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div> */}
          {analytics.dailyData && analytics.dailyData.length > 2 && (
            <BarChart analytics={analytics} />
          )}
        </>
      )}
    </div>
  )
}

export default Analytics
